import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Segment, Loader, Header, Message, Checkbox } from "semantic-ui-react";
import PageContainer from "components/lib/PageContainer";
import NotValidOption from "components/SiteManage/Content/lib/NotValidOption";
import OptionTakeoutStatus from "components/lib/OptionTakeoutStatus";
import { takeoutRepository } from "domain/repository";
import { UserContainer, StoreContainer } from "containers";
import { AGENCY_OPTION, StoreIdParam } from "config/raven";
import { StoreBreadcrumbBuilder } from "utils/breadcrumb";
import EcSettingForm from "components/SiteManage/Content/Takeout/EcSettingForm";
import InvoiceSettingForm from "components/SiteManage/Content/Takeout/InvoiceSettingForm";
import TakeoutAndEcCommonSettingForm from "components/SiteManage/Content/Takeout/TakeoutAndEcCommonSettingForm";
import { CustomShippingDestinationPayload } from "components/SiteManage/Content/Takeout/CustomShippingDestinationInputTable";
import TakeoutDiscountRuleForm from "components/SiteManage/Content/Takeout/TakeoutDiscountRuleForm";
import { TakeoutDiscountRule } from "domain/repository/takeout";

const AGENCY: AGENCY_OPTION = require("config/agency.json");

const parseCustomShippingDestinationsJson = (
  json?: string
): CustomShippingDestinationPayload[] => {
  if (!json || json.trim() === "") return [];
  const raw = JSON.parse(json);
  if (!Array.isArray(raw)) {
    throw new Error("データの形式が不正です");
  }
  return raw as CustomShippingDestinationPayload[];
};

const parseHiddenPrefectureCodesJson = (json?: string): string[] => {
  if (!json || json.trim() === "") return [];
  const raw = JSON.parse(json);
  if (!Array.isArray(raw)) {
    throw new Error("データの形式が不正です");
  }
  if (!raw.every((x) => typeof x === "string")) {
    throw new Error("データの形式が不正です");
  }
  return raw as string[];
};

const Container: React.FC = () => {
  const { isAgency } = UserContainer.useContainer();
  const { storeId } = useParams<StoreIdParam>();
  const { currentStore, changeCurrentStore } = StoreContainer.useContainer();

  const [loading, setLoading] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const [discountRulesLoading, setDiscountRulesLoading] =
    useState<boolean>(false);
  const [discountRulesError, setDiscountRulesError] = useState<string>("");
  const [discountRules, setDiscountRules] = useState<TakeoutDiscountRule[]>([]);
  const discountRulesRequestIdRef = useRef(0);

  const agencyEnabled = AGENCY[window.location.hostname].options.takeout;

  const useTakeoutEnabled = currentStore?.property?.useTakeout === true;

  useEffect(() => {
    if (!agencyEnabled) return;
    if (!useTakeoutEnabled) return;
    if (!storeId) return;

    // store切替時、前storeでのリクエストが遅れて完了した場合に
    // 前storeのルールがここに表示されないよう、最新リクエストのみ反映するガードを設ける
    const requestId = ++discountRulesRequestIdRef.current;

    setDiscountRulesLoading(true);
    setDiscountRulesError("");
    takeoutRepository
      .getTakeoutDiscountRules(isAgency, storeId)
      .then((rules: TakeoutDiscountRule[]) => {
        if (requestId !== discountRulesRequestIdRef.current) return;
        setDiscountRules(Array.isArray(rules) ? rules : []);
      })
      .catch((e: any) => {
        if (requestId !== discountRulesRequestIdRef.current) return;
        setDiscountRulesError(
          e?.response?.data?.message || "割引設定の取得に失敗しました"
        );
      })
      .finally(() => {
        if (requestId !== discountRulesRequestIdRef.current) return;
        setDiscountRulesLoading(false);
      });
  }, [agencyEnabled, isAgency, storeId, useTakeoutEnabled]);

  if (!agencyEnabled) {
    return <NotValidOption heading="テイクアウト機能" />;
  }

  if (!currentStore || loading) {
    return <Loader active />;
  }

  const handleClickEnable = (useAsEc: boolean) => {
    if (!storeId) {
      return;
    }
    setHasError(false);
    setLoading(true);
    takeoutRepository
      .enable(isAgency, storeId, useAsEc)
      .then(() => changeCurrentStore(isAgency, storeId))
      .catch(() => {
        setHasError(true);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const {
    useTakeout,
    takeoutUseAsEc,
    takeoutUseEligibleInvoice,
    ecCodFee,
    ecDefaultShippingFee,
    ecPrefectureShippingFeesJson,
    ecCustomShippingDestinationsJson,
    takeoutIssuerEnterpriseName,
    takeoutIssuerRegistrationNumber,
    takeoutNotesCart,
    takeoutNotesConfirm,
  } = currentStore.property;

  const customShippingDestinations = parseCustomShippingDestinationsJson(
    ecCustomShippingDestinationsJson?.toString()
  );

  return (
    <>
      <Header content="テイクアウト機能オプション利用状況" dividing />
      <OptionTakeoutStatus
        enabled={useTakeout}
        toEnable={handleClickEnable}
        isAgency={isAgency}
      />
      <br />
      {hasError && (
        <Message
          error
          content="テイクアウト機能の有効化に失敗しました。テイクアウト機能を利用できない場合は、運営にお問い合わせください。"
        />
      )}
      {useTakeout && (
        <>
          <TakeoutAndEcCommonSettingForm
            initialValues={{
              takeoutNotesCart: takeoutNotesCart ?? "",
              takeoutNotesConfirm: takeoutNotesConfirm ?? "",
            }}
            currentStore={currentStore}
            handleSubmit={async ({ values }) => {
              await takeoutRepository.setTakeoutAndEcCommonSettings(
                isAgency,
                storeId,
                values.takeoutNotesCart,
                values.takeoutNotesConfirm
              );
              window.location.reload();
              alert("設定を更新しました");
            }}
          />
          <div css={{ marginTop: 40 }}>
            <Header as="h4" content="購入金額に応じた自動割引" />
            {discountRulesLoading ? (
              <Loader active inline="centered" />
            ) : discountRulesError ? (
              <Message error content={discountRulesError} />
            ) : (
              <TakeoutDiscountRuleForm
                initialRules={discountRules}
                onSubmit={async (rules) => {
                  await takeoutRepository.updateTakeoutDiscountRules(
                    isAgency,
                    storeId,
                    rules
                  );
                  // 保存後は取得し直して表示を確定させる（サーバ側正規化を反映）
                  const refreshed =
                    await takeoutRepository.getTakeoutDiscountRules(
                      isAgency,
                      storeId
                    );
                  setDiscountRules(refreshed);
                  alert("設定を更新しました");
                }}
              />
            )}
          </div>
          {takeoutUseAsEc && (
            <>
              <div css={{ marginTop: 40 }}>
                <Checkbox
                  checked={takeoutUseAsEc}
                  label="簡易EC機能として利用"
                  disabled
                />
              </div>
              <EcSettingForm
                initialValues={{
                  ecCodFee: ecCodFee?.toString() ?? "0",
                  shippingFee: ecDefaultShippingFee?.toString(),
                  ecPrefectureShippingFeesJson:
                    ecPrefectureShippingFeesJson?.toString(),
                  ecHiddenPrefectureCodes: parseHiddenPrefectureCodesJson(
                    currentStore.property.ecHiddenPrefectureCodesJson?.toString()
                  ),
                  customShippingDestinations,
                }}
                currentStore={currentStore}
                handleSubmit={async ({ values }) => {
                  // 非表示にする都道府県コード
                  await takeoutRepository.setEcHiddenPrefectureCodes(
                    isAgency,
                    storeId,
                    values.ecHiddenPrefectureCodes || []
                  );

                  // カスタム配送先（都道府県/地域）：送信前にname未入力の行を除外
                  const filteredDestinations = (
                    values.customShippingDestinations || []
                  )
                    .filter((x) => (x?.name ?? "").toString().trim().length > 0)
                    .map((x) => ({
                      id: x.id,
                      name: x.name,
                      fee: x.fee ?? null,
                      after_type: x.after_type,
                      after_value: x.after_value,
                    }));

                  await takeoutRepository.setEcCustomShippingDestinations(
                    isAgency,
                    storeId,
                    filteredDestinations
                  );

                  // 基本送料が変更された場合
                  if (
                    parseInt(values.shippingFee, 10) !== ecDefaultShippingFee
                  ) {
                    await takeoutRepository.setEcDefaultShippingFee(
                      isAgency,
                      storeId,
                      parseInt(values.shippingFee, 10) || 0
                    );
                  }
                  // 都道府県別送料が変更された場合
                  if (
                    values.ecPrefectureShippingFeesJson !==
                    ecPrefectureShippingFeesJson
                  ) {
                    await takeoutRepository.setEcPrefectureShippingFee(
                      isAgency,
                      storeId,
                      values.ecPrefectureShippingFeesJson || "{}"
                    );
                  }
                  // 代引き手数料が変更された場合
                  if (parseInt(values.ecCodFee, 10) !== ecCodFee) {
                    await takeoutRepository.setEcCodFee(
                      isAgency,
                      storeId,
                      parseInt(values.ecCodFee, 10) || 0
                    );
                  }
                  window.location.reload();
                  alert("設定を更新しました");
                }}
              />
            </>
          )}
          <br />
          <>
            <InvoiceSettingForm
              initialValues={{
                useInvoice: takeoutUseEligibleInvoice,
                issuerName: takeoutIssuerEnterpriseName?.toString(),
                issuerNumber: takeoutIssuerRegistrationNumber?.toString(),
              }}
              currentStore={currentStore}
              handleSubmit={({ values }) => {
                takeoutRepository
                  .setInvoiceSettings(
                    isAgency,
                    storeId,
                    values.useInvoice,
                    values.issuerName,
                    values.issuerNumber
                  )
                  .then(() => {
                    window.location.reload();
                    alert("適格簡易請求書の設定を更新しました");
                  })
                  .catch((e) => {
                    alert(e.response.data.message);
                  });
              }}
            />
          </>
        </>
      )}
    </>
  );
};

const Takeout = () => {
  const { isAgency } = UserContainer.useContainer();
  const { storeId } = useParams<StoreIdParam>();

  return (
    <PageContainer
      header="テイクアウト機能"
      breadcrumbs={new StoreBreadcrumbBuilder(isAgency, storeId)
        .siteManagePage()
        .build("テイクアウト機能")}
    >
      <Segment padded>
        <Container />
      </Segment>
    </PageContainer>
  );
};

export default Takeout;
